import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "../utils/axios";
import {
    Box,
    Heading,
    Spinner,
    Alert,
    AlertTitle,
    Text,
    Flex,
    SimpleGrid,
    Icon,
    Button,
    VStack
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";

const ChooseLocation = () => {
    const API_URL = process.env.REACT_APP_API_URL;
    const { setLocation, currLocation, locations, setLocations } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        const fetchLocations = async (isRetry = false) => {
            try {
                const response = await axios.get(`${API_URL}/users/locations`);
                if (cancelled) return;

                const locationData = response.data.locations || [];
                setLocations(locationData);
                setError(null);

                if (locationData.length === 1) {
                    setLocation(locationData[0].id);
                    navigate("/");
                }
                setLoading(false);
            } catch (err) {
                if (cancelled) return;

                if (!isRetry) {
                    // Transient hiccups (cold backend start, a token refresh
                    // still in flight) are common right after a page reload -
                    // give it one quiet retry before surfacing an error.
                    setTimeout(() => fetchLocations(true), 1500);
                    return;
                }
                setError("Failed to load locations.");
                setLoading(false);
            }
        };

        setLoading(true);
        fetchLocations();

        return () => {
            cancelled = true;
        };
    }, [retryCount]);

    const handleRetry = () => {
        setError(null);
        setRetryCount((count) => count + 1);
    };

    const handleLocationSelect = (locationId) => {
        setLocation(locationId);
        navigate("/");
    };

    if (loading) {
        return (
            <Flex justifyContent="center" alignItems="center" minH="100vh">
                <Spinner size="xl" color="brand.700" />
            </Flex>
        );
    }

    if (error) {
        return (
            <Flex justifyContent="center" alignItems="center" minH="100vh">
                <VStack spacing={4} maxW="400px">
                    <Alert status="error" borderRadius="md">
                        <AlertTitle>{error}</AlertTitle>
                    </Alert>
                    <Button bg="brand.600" color="white" _hover={{ bg: "brand.700" }} onClick={handleRetry}>
                        Pokušaj ponovno
                    </Button>
                </VStack>
            </Flex>
        );
    }

    return (
        <Box
            as="main"
            pt={currLocation ? { base: "24px", md: "20px" } : { base: "20px", md: "20px" }}
            pl={currLocation ? { base: "0px", md: "240px" } : "0"}
            pr={{ base: "0px", md: "30px" }}
            pb="20px"
            minH="100vh"
            maxW={currLocation ? undefined : "1200px"}
            mx={currLocation ? undefined : "auto"}
        >
            <Flex justify={currLocation ? "flex-start" : "center"} mb={6} px={4}>
                <Heading color="brand.700" size="lg">Odabir lokacije</Heading>
            </Flex>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4} px={4}>
                {locations.map((location) => (
                    <Flex
                        key={location.id}
                        align="center"
                        gap={3}
                        p={4}
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.100"
                        boxShadow="card"
                        bg="white"
                        cursor="pointer"
                        transition="all 0.15s ease-out"
                        _hover={{ bg: "brand.50", borderColor: "brand.200" }}
                        onClick={() => handleLocationSelect(location.id)}
                    >
                        <Flex
                            align="center"
                            justify="center"
                            boxSize="40px"
                            borderRadius="lg"
                            bg="brand.50"
                            color="brand.600"
                            flexShrink={0}
                        >
                            <Icon as={FaMapMarkerAlt} boxSize={4} />
                        </Flex>
                        <Text fontSize="md" fontWeight="700" color="brand.700" noOfLines={1}>
                            {location.name}
                        </Text>
                    </Flex>
                ))}
            </SimpleGrid>
        </Box>
    );
};

export default ChooseLocation;
